import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import HelpTooltip from '@/components/help/HelpTooltip';
import helpContent from '@/help/helpContent';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Server, Plus, Wifi, RefreshCw, Link2, Shield, Archive, ChevronRight,
  AlertTriangle, CheckCircle, X, Wrench, FileText, MessageCircle, History, Unlink
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700', connected: 'bg-emerald-50 text-emerald-700',
  error: 'bg-red-50 text-red-700', disabled: 'bg-gray-100 text-gray-500', archived: 'bg-gray-100 text-gray-400',
};

const SYNC_TYPE_LABELS = {
  connection_test: 'Prueba de conexión',
  full: 'Sincronización completa',
};

const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function MCPConnectorCenter() {
  const navigate = useNavigate();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list');
  const [form, setForm] = useState({ key: '', name: '', transport: 'http', base_url: '', auth_mode: 'none', environment: 'sandbox' });
  const [detail, setDetail] = useState(null);
  const [tools, setTools] = useState([]);
  const [resources, setResources] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [unmapTarget, setUnmapTarget] = useState(null);
  const [unmapping, setUnmapping] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/tenant/mcp/servers'); setServers(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try { await api.post('/tenant/mcp/servers', form); toast.success('Servidor creado'); setTab('list'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleTest = async (id) => {
    try {
      const { data } = await api.post(`/tenant/mcp/servers/${id}/test-connection`);
      if (data.ok) toast.success(`Conectado (${data.latency_ms}ms)`);
      else toast.error(`No se pudo conectar: ${data.error || 'sin detalle'}`);
      load(); if (detail?.id === id) loadDetail(id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleSync = async (id) => {
    try {
      const { data } = await api.post(`/tenant/mcp/servers/${id}/sync`);
      toast.success(`Sincronizado: ${data.tools_synced} tools, ${data.resources_synced} resources, ${data.prompts_synced} prompts`);
      load(); if (detail?.id === id) loadDetail(id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleArchive = async (id) => {
    try { await api.patch(`/tenant/mcp/servers/${id}/archive`); toast.success('Archivado'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleMap = async (mcpToolId) => {
    try { const { data } = await api.post(`/tenant/mcp/tools/${mcpToolId}/map-to-ai-tool`); toast.success(`Mapeado → ${data.ai_tool_key}`); if (detail) loadDetail(detail.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleUnmap = async () => {
    if (!unmapTarget) return;
    setUnmapping(true);
    try {
      const { data } = await api.post(`/tenant/mcp/tools/${unmapTarget.id}/unmap`);
      if (data?.ok) toast.success('Herramienta desvinculada');
      else toast.error('No se pudo desvincular la herramienta');
      setUnmapTarget(null);
      if (detail) loadDetail(detail.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo desvincular la herramienta');
    } finally { setUnmapping(false); }
  };

  // Historial: el backend devuelve los registros de TODOS los servidores,
  // así que filtramos por el servidor abierto.
  const loadLogs = async (serverId) => {
    setLogsLoading(true); setLogsError(null);
    try {
      const { data } = await api.get('/tenant/mcp/logs', { params: { page: 1, page_size: 50 } });
      setLogs((data || []).filter(l => l.mcp_server_id === String(serverId)));
    } catch (e) {
      setLogs([]);
      setLogsError(formatApiError(e.response?.data?.detail) || 'No se pudo cargar el historial.');
    } finally { setLogsLoading(false); }
  };

  const loadDetail = async (id) => {
    try {
      const [srv, t, r, p] = await Promise.all([
        api.get(`/tenant/mcp/servers/${id}`),
        api.get(`/tenant/mcp/servers/${id}/tools`),
        api.get(`/tenant/mcp/servers/${id}/resources`),
        api.get(`/tenant/mcp/servers/${id}/prompts`),
      ]);
      setDetail(srv.data); setTools(t.data || []); setResources(r.data || []); setPrompts(p.data || []);
      setTab('detail');
      loadLogs(id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-5" data-testid="mcp-connector-center">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2"><Server className="w-5 h-5" /> Conectores MCP</h2>
          <p className="text-sm text-[#64748B] mt-0.5">{servers.length} servidor{servers.length !== 1 ? 'es' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate('/tenant/ai/tools')} data-testid="back-to-tools"><Wrench className="w-4 h-4 mr-1" /> Herramientas</Button>
          <Button size="sm" onClick={() => setTab('create')} data-testid="mcp-create-btn"><Plus className="w-4 h-4 mr-1" /> Nuevo servidor</Button>
        </div>
      </div>

      <SectionHelp module="mcp" title={helpContent.mcp.title} description={helpContent.mcp.description} whatItDoesNot={helpContent.mcp.whatItDoesNot} />
      <VerticalExampleBox module="mcp" />

      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 flex items-start gap-2">
        <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        Este sprint registra conectores MCP y sincroniza su catálogo. Ejecución gobernada disponible en Orchestrator (Config IA &rarr; Orchestrator).
      </div>

      <button onClick={() => navigate('/tenant/integrations?category=mcp')}
        className="flex items-center gap-1.5 text-xs text-[#64748B] hover:text-[#0F172A] transition-colors" data-testid="mcp-integrations-cta">
        <Link2 className="w-3.5 h-3.5" /> Configura credenciales MCP en Integraciones
      </button>

      {/* LIST */}
      {tab === 'list' && (
        <div className="space-y-2" data-testid="mcp-server-list">
          {servers.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
              <Server className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-sm text-[#64748B] mb-3">No hay servidores MCP registrados.</p>
              <Button size="sm" onClick={() => setTab('create')}><Plus className="w-4 h-4 mr-1" /> Registrar servidor</Button>
            </div>
          ) : servers.map(s => (
            <div key={s.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3 hover:border-[#94A3B8] transition-colors cursor-pointer"
              onClick={() => loadDetail(s.id)} data-testid={`mcp-server-${s.id}`}>
              <div className="w-9 h-9 rounded-lg bg-[#F8F9FA] flex items-center justify-center flex-shrink-0"><Server className="w-4 h-4 text-[#64748B]" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A] truncate">{s.name}</p>
                <p className="text-xs text-[#94A3B8]">{s.key} · {s.transport} · {s.environment}</p>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[s.status] || 'bg-gray-100'}`}>{s.status}</span>
              <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
            </div>
          ))}
        </div>
      )}

      {/* CREATE */}
      {tab === 'create' && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4 max-w-lg" data-testid="mcp-create-form">
          <h3 className="text-sm font-bold">Nuevo Servidor MCP</h3>
          <Input placeholder="Key único" value={form.key} onChange={e => setForm(f => ({...f, key: e.target.value}))} data-testid="mcp-key" />
          <Input placeholder="Nombre" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="mcp-name" />
          <Input placeholder="URL base (https://...)" value={form.base_url} onChange={e => setForm(f => ({...f, base_url: e.target.value}))} data-testid="mcp-url" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Transporte</label>
              <select value={form.transport} onChange={e => setForm(f => ({...f, transport: e.target.value}))} className="w-full text-xs border rounded-lg px-3 py-2" data-testid="mcp-transport">
                <option value="http">HTTP</option><option value="sse">SSE</option><option value="streamable_http">Streamable HTTP</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Auth</label>
              <select value={form.auth_mode} onChange={e => setForm(f => ({...f, auth_mode: e.target.value}))} className="w-full text-xs border rounded-lg px-3 py-2" data-testid="mcp-auth">
                <option value="none">Ninguna</option><option value="bearer">Bearer Token</option><option value="api_key_header">API Key Header</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setTab('list')}>Cancelar</Button>
            <Button size="sm" onClick={handleCreate} data-testid="mcp-save">Crear</Button>
          </div>
        </div>
      )}

      {/* DETAIL */}
      {tab === 'detail' && detail && (
        <div className="space-y-4" data-testid="mcp-detail">
          <div className="flex items-center gap-2">
            <button onClick={() => { setTab('list'); setDetail(null); }} className="text-xs text-[#64748B] hover:text-[#0F172A]">← Volver</button>
            <h3 className="text-sm font-bold text-[#0F172A]">{detail.name}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[detail.status]}`}>{detail.status}</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => handleTest(detail.id)} data-testid="mcp-test-connection"><Wifi className="w-4 h-4 mr-1" /> Probar Conexión</Button>
            <Button size="sm" variant="outline" onClick={() => handleSync(detail.id)} data-testid="mcp-sync-btn"><RefreshCw className="w-4 h-4 mr-1" /> Sincronizar</Button>
            {detail.status !== 'archived' && (
              <Button size="sm" variant="outline" onClick={() => { handleArchive(detail.id); setTab('list'); }} className="text-red-600"><Archive className="w-4 h-4 mr-1" /> Archivar</Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-white border rounded-lg p-3"><span className="text-[#94A3B8]">Transport:</span> {detail.transport}</div>
            <div className="bg-white border rounded-lg p-3"><span className="text-[#94A3B8]">URL:</span> {detail.base_url || '—'}</div>
            <div className="bg-white border rounded-lg p-3"><span className="text-[#94A3B8]">Auth:</span> {detail.auth_mode}</div>
            <div className="bg-white border rounded-lg p-3"><span className="text-[#94A3B8]">Último sync:</span> {detail.last_sync_at ? fmtDateTime(detail.last_sync_at) : 'Nunca'}</div>
          </div>

          {detail.last_error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 flex items-start gap-2" data-testid="mcp-last-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">El último intento de conexión falló</p>
                <p className="mt-0.5 break-words">{detail.last_error}</p>
                <p className="mt-1 text-red-600">Revisa la URL y las credenciales en Integraciones, y vuelve a usar “Probar Conexión”.</p>
              </div>
            </div>
          )}

          {/* Tools */}
          <div className="bg-white border rounded-xl p-4">
            <h4 className="text-xs font-bold mb-2 flex items-center gap-1">
              <Wrench className="w-3.5 h-3.5" /> Tools ({tools.length})
              <HelpTooltip text="Son las acciones que ofrece este servidor externo. Al vincular una (botón “Mapear”), queda disponible como Herramienta de IA para tus automatizaciones. Si la desvinculas, deja de poder ejecutarse." />
            </h4>
            {tools.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin tools. Ejecuta Sincronizar.</p> :
              tools.map(t => (
                <div key={t.id} className="flex items-center gap-2 py-1.5 border-b last:border-0">
                  <span className="text-xs text-[#0F172A] flex-1">{t.external_tool_name}</span>
                  <span className="text-[10px] text-[#94A3B8]">{t.description?.substring(0, 40)}</span>
                  {t.mapped_ai_tool_id ? (
                    <>
                      <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 rounded">Mapeado</span>
                      <Button size="sm" variant="outline" className="text-[10px] h-6 px-2 text-red-600"
                        onClick={() => setUnmapTarget(t)} data-testid={`mcp-unmap-tool-btn-${t.id}`}>
                        <Unlink className="w-3 h-3 mr-1" /> Desvincular
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={() => handleMap(t.id)} data-testid="mcp-map-tool-btn">Mapear</Button>
                  )}
                </div>
              ))
            }
          </div>

          {/* Resources */}
          <div className="bg-white border rounded-xl p-4">
            <h4 className="text-xs font-bold mb-2 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Resources ({resources.length})</h4>
            {resources.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin resources.</p> :
              resources.map(r => (
                <div key={r.id} className="flex items-center gap-2 py-1.5 border-b last:border-0">
                  <span className="text-xs text-[#0F172A] flex-1">{r.name || r.resource_uri}</span>
                  <span className="text-[10px] text-[#94A3B8]">{r.mime_type || '—'}</span>
                </div>
              ))
            }
          </div>

          {/* Prompts */}
          <div className="bg-white border rounded-xl p-4">
            <h4 className="text-xs font-bold mb-2 flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> Prompts ({prompts.length})</h4>
            {prompts.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin prompts.</p> :
              prompts.map(p => (
                <div key={p.id} className="flex items-center gap-2 py-1.5 border-b last:border-0">
                  <span className="text-xs text-[#0F172A] flex-1">{p.external_prompt_name}</span>
                  <span className="text-[10px] text-[#94A3B8]">{p.description?.substring(0, 50)}</span>
                </div>
              ))
            }
          </div>

          {/* Historial de sincronización */}
          <div className="bg-white border rounded-xl p-4" data-testid="mcp-sync-history">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h4 className="text-xs font-bold flex items-center gap-1">
                <History className="w-3.5 h-3.5" /> Historial de sincronización
                <HelpTooltip text="Cada vez que pruebas la conexión o sincronizas, se guarda aquí el resultado: si funcionó o falló, cuánto tardó, cuántas herramientas trajo y el mensaje de error exacto cuando algo sale mal. Sirve para saber por qué este conector dejó de responder." />
              </h4>
              <Button size="sm" variant="outline" className="text-[10px] h-6 px-2"
                onClick={() => loadLogs(detail.id)} disabled={logsLoading} data-testid="mcp-logs-refresh">
                <RefreshCw className={`w-3 h-3 mr-1 ${logsLoading ? 'animate-spin' : ''}`} /> Actualizar
              </Button>
            </div>

            {logsLoading ? (
              <p className="text-xs text-[#94A3B8]">Cargando historial…</p>
            ) : logsError ? (
              <div className="text-xs text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="flex-1">{logsError}</span>
                <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" onClick={() => loadLogs(detail.id)} data-testid="mcp-logs-retry">Reintentar</Button>
              </div>
            ) : logs.length === 0 ? (
              <p className="text-xs text-[#94A3B8]">Sin registros todavía. Usa “Probar Conexión” o “Sincronizar” para generar el primero.</p>
            ) : (
              <div className="space-y-1.5">
                {logs.map(l => (
                  <div key={l.id} className="border-b last:border-0 pb-1.5 last:pb-0" data-testid={`mcp-log-${l.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {l.status === 'success' ? (
                        <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Correcto</span>
                      ) : (
                        <span className="text-[10px] bg-red-50 text-red-700 px-1.5 py-0.5 rounded flex items-center gap-1"><X className="w-3 h-3" /> Falló</span>
                      )}
                      <span className="text-xs text-[#0F172A]">{SYNC_TYPE_LABELS[l.sync_type] || l.sync_type}</span>
                      <span className="text-[10px] text-[#94A3B8] flex-1">{fmtDateTime(l.created_at)}</span>
                      {l.duration_ms != null && <span className="text-[10px] text-[#94A3B8]">{l.duration_ms} ms</span>}
                    </div>
                    {l.sync_type === 'full' && (
                      <p className="text-[10px] text-[#64748B] mt-0.5">
                        {l.tools_count || 0} tools · {l.resources_count || 0} resources · {l.prompts_count || 0} prompts
                      </p>
                    )}
                    {l.error_message && (
                      <p className="text-[10px] text-red-600 mt-0.5 break-words">{l.error_message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmación de desvinculación */}
      <Dialog open={!!unmapTarget} onOpenChange={(open) => { if (!open && !unmapping) setUnmapTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="mcp-unmap-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Desvincular herramienta</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              Vas a desvincular <span className="font-medium text-[#0F172A]">{unmapTarget?.external_tool_name}</span> de tus Herramientas de IA.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Las automatizaciones que ya la usan dejarán de poder ejecutarla. Seguirá apareciendo en la lista de
              Herramientas de IA, pero no funcionará hasta que la vuelvas a vincular con el botón “Mapear”.
            </span>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setUnmapTarget(null)} disabled={unmapping} data-testid="mcp-unmap-cancel">Cancelar</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={handleUnmap} disabled={unmapping} data-testid="mcp-unmap-confirm">
              {unmapping ? 'Desvinculando…' : 'Sí, desvincular'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
